import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { useToast } from "../../context/ToastContext";
import { fetchQuote, useCatalog, useOrganizations, useRefreshAll, useRooms } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { localName } from "../../lib/format";
import type { PaymentMethod, Quote } from "../../lib/types";
import { SectionHeader } from "../ui/Badges";
import { DownloadButton } from "../ui/DownloadButton";
import { PrintButton } from "../ui/PrintButton";
import { PaymentPicker } from "../ui/PaymentPicker";
import { AddonChecklist } from "./AddonChecklist";
import { PackagePicker } from "./PackagePicker";
import { PriceBreakdown } from "./PriceBreakdown";

type BookingType = "birthday" | "school_trip" | "corporate";
const TYPES: BookingType[] = ["birthday", "school_trip", "corporate"];

function todayStr(offsetDays = 0): string {
  const d = new Date(Date.now() + offsetDays * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function BookingForm() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const refresh = useRefreshAll();
  const catalog = useCatalog();
  const rooms = useRooms();
  const orgs = useOrganizations();

  const [type, setType] = useState<BookingType>("birthday");
  const [host, setHost] = useState({ fullName: "", phone: "" });
  const [orgId, setOrgId] = useState("");
  const [honoree, setHoneree] = useState({ name: "", age: "" });
  const [roomId, setRoomId] = useState("");
  const [date, setDate] = useState(todayStr(1));
  const [time, setTime] = useState("16:00");
  const [kids, setKids] = useState(15);
  const [adults, setAdults] = useState(10);
  const [supervisors, setSupervisors] = useState(0);
  const [pkgId, setPkgId] = useState("");
  const [addons, setAddons] = useState<Record<string, number>>({});
  const [depositStr, setDepositStr] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("card");
  const [onCredit, setOnCredit] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ id: string; refNo: string } | null>(null);

  const perStudent = type !== "birthday";
  const packages = useMemo(() => (catalog.data ?? []).filter((p) => p.kind === (perStudent ? "school_package" : "party_package")).sort((a, b) => a.price - b.price), [catalog.data, perStudent]);
  const partyAddons = useMemo(() => (catalog.data ?? []).filter((p) => p.kind === "party_addon"), [catalog.data]);
  const pkg = packages.find((p) => p.id === pkgId);

  // Default selections follow the booking type.
  useEffect(() => {
    setPkgId(packages[Math.min(1, packages.length - 1)]?.id ?? "");
    setOnCredit(type !== "birthday");
  }, [packages, type]);
  useEffect(() => {
    if (!roomId && rooms.data?.[0]) setRoomId(rooms.data[0].id);
  }, [rooms.data, roomId]);

  const addonList = useMemo(() => Object.entries(addons).filter(([, q]) => q > 0).map(([productId, qty]) => ({ productId, qty })), [addons]);

  // Live quote from the server (single source of truth for pricing and tax).
  useEffect(() => {
    if (!pkgId || kids < 1) {
      setQuote(null);
      return;
    }
    setQuoting(true);
    const h = setTimeout(() => {
      fetchQuote({ type, packageProductId: pkgId, kidsCount: kids, addons: addonList })
        .then(setQuote)
        .catch(() => setQuote(null))
        .finally(() => setQuoting(false));
    }, 250);
    return () => clearTimeout(h);
  }, [type, pkgId, kids, addonList]);

  const deposit = depositStr.trim() === "" || Number.isNaN(Number(depositStr)) ? 0 : jod(Number(depositStr));
  const room = rooms.data?.find((r) => r.id === roomId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!pkg) return;
    setBusy(true);
    try {
      const start = new Date(`${date}T${time}`);
      const end = new Date(start.getTime() + (pkg.durationMinutes ?? 150) * 60_000);
      const res = await api.post<{ booking: { id: string; refNo: string } }>("/bookings", {
        type,
        guardian: type === "birthday" ? { fullName: host.fullName.trim(), phone: host.phone, locale } : undefined,
        organizationId: perStudent ? orgId : undefined,
        honoreeName: type === "birthday" ? honoree.name.trim() || undefined : undefined,
        honoreeAge: type === "birthday" && honoree.age ? Number(honoree.age) : undefined,
        roomId: roomId || undefined,
        slotStart: start.toISOString(),
        slotEnd: end.toISOString(),
        kidsCount: kids,
        adultsCount: adults,
        supervisorsFree: supervisors,
        packageProductId: pkg.id,
        addons: addonList,
        deposit: deposit > 0 ? [{ method: method === "credit" ? "cash" : method, amount: deposit }] : [],
        balanceOnCredit: onCredit && (perStudent || method === "credit"),
      });
      setCreated(res.booking);
      toast(t("booking.saved", { ref: res.booking.refNo }), "success");
      refresh();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setCreated(null);
    setHost({ fullName: "", phone: "" });
    setHoneree({ name: "", age: "" });
    setAddons({});
    setDepositStr("");
  }

  const valid = !!pkg && kids >= 1 && !!date && !!time && (perStudent ? !!orgId : host.fullName.trim().length >= 2 && host.phone.replace(/\D/g, "").length >= 8) && (!room || kids <= room.maxKids) && (!quote || deposit <= quote.total);

  return (
    <form onSubmit={submit} className="card space-y-4 p-4 sm:p-5" aria-label={t("booking.title")}>
      <SectionHeader title={`🚀 ${t("booking.title")}`} right={
        <div role="tablist" className="flex rounded-xl bg-gray-100 p-1 text-[11px] font-black">
          {TYPES.map((x) => (
            <button key={x} type="button" role="tab" aria-selected={type === x} onClick={() => { setType(x); setCreated(null); }} className={`rounded-lg px-2.5 py-1 ${type === x ? "bg-cosmic-purple text-white" : "text-gray-600"}`}>{t(`booking.type.${x}`)}</button>
          ))}
        </div>
      } />

      {created && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-bold text-emerald-800" role="status">
          <span>✅ {t("booking.saved", { ref: created.refNo })}</span>
          <span className="flex gap-2">
            <DownloadButton path={`/documents/invoice/booking/${created.id}?locale=${locale}`} fallbackName={`Invoice-${created.refNo}.pdf`} className="btn-ghost">📄 {t("booking.downloadInvoice")}</DownloadButton>
            <PrintButton path={`/documents/invoice/booking/${created.id}?locale=${locale}`} />
            <button type="button" className="btn-dark" onClick={reset}>{t("booking.newBooking")}</button>
          </span>
        </div>
      )}

      {type === "birthday" ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div><label className="label" htmlFor="b-host">{t("booking.hostParent")}</label><input id="b-host" className="field" value={host.fullName} onChange={(e) => setHost({ ...host, fullName: e.target.value })} required /></div>
          <div><label className="label" htmlFor="b-phone">{t("booking.hostPhone")}</label><input id="b-phone" className="field num font-mono" type="tel" value={host.phone} onChange={(e) => setHost({ ...host, phone: e.target.value })} required /></div>
          <div><label className="label" htmlFor="b-kid">{t("booking.honoree")}</label><input id="b-kid" className="field" value={honoree.name} onChange={(e) => setHoneree({ ...honoree, name: e.target.value })} /></div>
          <div><label className="label" htmlFor="b-age">{t("booking.turningAge")}</label><input id="b-age" className="field num font-mono" type="number" min={1} max={18} value={honoree.age} onChange={(e) => setHoneree({ ...honoree, age: e.target.value })} /></div>
        </div>
      ) : (
        <div>
          <label className="label" htmlFor="b-org">{t("booking.organization")}</label>
          <select id="b-org" className="field" value={orgId} onChange={(e) => setOrgId(e.target.value)} required>
            <option value="">{t("booking.selectOrganization")}</option>
            {orgs.data?.map((o) => <option key={o.id} value={o.id}>{localName(o, locale)}</option>)}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <div><label className="label" htmlFor="b-date">{t("booking.date")}</label><input id="b-date" className="field num font-mono" type="date" min={todayStr(0)} value={date} onChange={(e) => setDate(e.target.value)} required /></div>
        <div><label className="label" htmlFor="b-time">{t("booking.startTime")}</label><input id="b-time" className="field num font-mono" type="time" value={time} onChange={(e) => setTime(e.target.value)} required /></div>
        <div>
          <label className="label" htmlFor="b-room">{t("booking.room")}</label>
          <select id="b-room" className="field" value={roomId} onChange={(e) => setRoomId(e.target.value)}>
            {rooms.data?.map((r) => <option key={r.id} value={r.id}>{localName(r, locale)} ({t("booking.max", { count: r.maxKids })})</option>)}
          </select>
        </div>
        <div><label className="label" htmlFor="b-kids">{perStudent ? t("booking.students") : t("booking.kids")}</label><input id="b-kids" className="field num font-mono" type="number" min={1} max={500} value={kids} onChange={(e) => setKids(Number(e.target.value))} required /></div>
        {perStudent ? (
          <div><label className="label" htmlFor="b-sup">{t("booking.supervisors")}</label><input id="b-sup" className="field num font-mono" type="number" min={0} value={supervisors} onChange={(e) => setSupervisors(Number(e.target.value))} /></div>
        ) : (
          <div><label className="label" htmlFor="b-adults">{t("booking.adults")}</label><input id="b-adults" className="field num font-mono" type="number" min={0} value={adults} onChange={(e) => setAdults(Number(e.target.value))} /></div>
        )}
      </div>
      {room && kids > room.maxKids && <p className="text-[11px] font-bold text-red-600" role="alert">⚠️ {t("errors.ROOM_TOO_SMALL", { max: room.maxKids })}</p>}

      <PackagePicker packages={packages} value={pkgId} onChange={setPkgId} perStudent={perStudent} />

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-12">
        <div className="space-y-3 md:col-span-6">
          <AddonChecklist addons={partyAddons} selected={addons} onToggle={(id, on) => setAddons((a) => ({ ...a, [id]: on ? 1 : 0 }))} />
          <div>
            <label className="label" htmlFor="b-dep">{t("booking.depositAmount")}</label>
            <input id="b-dep" className="field num font-mono" inputMode="decimal" value={depositStr} onChange={(e) => setDepositStr(e.target.value)} placeholder="0.000" />
          </div>
          <div>
            <span className="label">{t("booking.depositMethod")}</span>
            <PaymentPicker value={method} onChange={setMethod} methods={["card", "cash", "cliq"]} />
          </div>
          <label className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-xs font-black text-amber-900">
            <input type="checkbox" className="rounded text-cosmic-purple focus:ring-0" checked={onCredit} onChange={(e) => setOnCredit(e.target.checked)} />
            🏛️ {t("booking.balanceOnCredit")}
          </label>
        </div>
        <div className="space-y-3 md:col-span-6">
          <PriceBreakdown quote={quote} deposit={deposit} loading={quoting} />
          <button type="submit" className="btn-primary w-full !py-3 text-sm" disabled={!valid || busy}>{busy ? t("common.loading") : t("booking.save")}</button>
        </div>
      </div>
    </form>
  );
}
